import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { SummaryReportDto } from './summary-report.service';

// Notes
export interface SaveNoteNotificationRequest {
  notes: string;
  notesStartDate: string;
  notesEndDate: string;
  projectId: number;
  projectName: string;
}

// Rigs
export interface RigDto {
  id: number;
  name: string;
}

export interface AddRigRequest {
  rigName: string;
}

// Metrics
export interface SbuDto {
  sbuCode: string;
  sbuName: string;
}

export interface MetricsItemDto {
  name: string;
  count: number;
}

export interface MetricsGroupDto {
  name: string;
  count: number;
  children: MetricsItemDto[];
}

export interface TestMetricsResultDto {
  total: number;
  groups: MetricsGroupDto[];
}

// Well Update
export interface WellDto {
  id: number;
  name: string;
  wellLocation: string | null;
  sbuCode: string;
  sbuName: string | null;
}

export interface WellUpdateProjectDto {
  id: number;
  name: string;
  wellId: number;
  sbuCode: string;
}

export interface ChangeProjectWellRequest {
  wellId: number;
}

export interface UpdateWellInfoRequest {
  wellName: string;
  wellLocation: string;
  sbuCode: string;
}

// BU Redesign Metrics
export interface BuRedesignMetricsResultDto {
  from: string;
  to: string;
  buCode: string;
  statistics: BusinessUnitStatisticsDto[];
}
export interface BusinessUnitStatisticsDto {
  buName: string;
  slurryStatistics: SlurryStatisticsDto;
  projectStatistics: ProjectStatisticsDto;
}
export interface SlurryStatisticsDto {
  totalCount: number;
  baseStatistics: SlurryBaseStatisticsDto;
  redesignStatistics: SlurryRedesignStatisticsDto;
}
export interface ProjectStatisticsDto {
  totalCount: number;
  baseCount: number;
  redesignsCount: number;
}
export interface SlurryBaseStatisticsDto {
  totalCount: number;
  fromBaseProjectsCount: number;
  fromRedesignProjectsCount: number;
}

export interface SlurryRedesignStatisticsDto {
  totalCount: number;
  slurryRedesigns: SlurryRedesignTypeStatisticsDto[];
}
export interface TestStatisticsDto {
  totalCount: number;
  testName: string;
}

export interface SlurryRedesignTypeStatisticsDto {
  totalCount: number;
  redesignReason: string;
  tests: TestStatisticsDto[];
}

// Search
export interface ProjectSearchRequest {
  projectName?: string;
  wellLocation?: string;
  wellName?: string;
  pipeSize?: string;
  rigName?: string;
  jobTypeId?: number;
  testType?: number;
  serviceCompanyId?: number;
  mdFrom?: number;
  mdTo?: number;
  tvdFrom?: number;
  tvdTo?: number;
  bhctFrom?: number;
  bhctTo?: number;
  bhstFrom?: number;
  bhstTo?: number;
  finalPressureTo?: number;
  finalPressureFrom?: number;
  thickeningTimeFrom?: number;
  thickeningTimeTo?: number;
  densityFrom?: number;
  densityTo?: number;
  receivedFrom?: string;
  receivedTo?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  materialName?: string;
}

export interface ProjectSearchResult {
  projectId: number;
  status: string;
  projectName: string;
  sbuCode: string;
  wellName: string | null;
  pipeSize: string | null;
  md: number | null;
  tvd: number | null;
  jobType: string;
  jobSubType: string;
  mudType: string;
  isBlendTest: boolean;
  receivedBy: string;
  receiveDate: string;
  sentToLabDate: string | null;
  expectedDueDate: string | null;
}

export interface MaterialsQaqcDto {
  id: number;
  name: string;
  type: number; // MaterialTypes enum
  cisProContainerId: string;
  byWeightOfTypeID: number;
  cisProLocation: string;
  specificGravity?: number | null;
  ph?: number | null;
  chlorides?: number | null;
  comments: string;
  sampleRefNum: string;
  recvAmount?: number | null;
  currentAmount?: number | null;
  minAmount?: number | null;
  rigName: string;
  recvDate?: string | null;
  notifyAtReorder?: boolean | null;
  trackSample?: boolean | null;
  lastUsedDate?: string | null;
  lastEmailDate?: string | null;
  updateUser: string;
  surryName: string;
  materialId: number;
  containerWeightId?: number | null;
  contWeightAmount?: number | null;
  currentDate: string;
  otherWeight?: number | null;
}

export interface MaterialsQaqcSearchParams {
  materialName?: string;
  barcodeId?: string;
  sampleRefNum?: string;
  rigName?: string;
}
// Email Subscriptions
export interface EmailSubscriptionItemDto {
  emailTypeId: number;
  displayName: string;
  isSelected: boolean;
  isReadOnly: boolean;
}

export interface EmailSubscriptionsResponseDto {
  subscriptions: EmailSubscriptionItemDto[];
}
// Materials Management
export interface GetMaterialsForSearchResultDto {
  materialId: number;
  materialName: string | null;
  functionName: string | null;
  specificGravity: number | null;
  materialTypeID: number;
  sackWeightLb: number | null;
  addAfterStirring: boolean;
  isActive: boolean | null;
  isFluidLossTestWithFoamNeeded: boolean | null;
}

export interface SearchMaterialsRequest {
  searchTerm: string;
  materialType?: number;
}

export interface CreateMaterialRequest {
  name: string;
  functionName: string;
  materialTypes: number;
  specificGravity: number | null;
  sackWeight: number | null;
  addAfterStirring: boolean;
  isActive: boolean;
  isFluidLossTestWithFoamNeeded: boolean;
}

export interface MaterialBriefDto {
  id: number;
  name: string;
  functionName: string;
  materialTypes: number;
  specificGravity: number | null;
  sackWeight: number | null;
  addAfterStirring: boolean;
  isActive: boolean;
  isFluidLossTestWithFoamNeeded: boolean;
}

export interface UpdateMaterialRequestQaqc {
  id: number;
  cisProContainerId?: string;
  specificGravity?: number | null;
  ph?: number | null;
  chlorides?: number | null;
  sampleRefNum?: string;
  recvAmount?: number | null;
  currentAmount?: number | null;
  minAmount?: number | null;
  rigName?: string;
  recvDate?: string | null;
  comments?: string;
  notifyAtReorder?: boolean;
  trackSample?: boolean;
  contWeightAmount?: number | null;
  otherWeight?: number | null;
}

export interface AddMaterialRequestQaqc {
  materialId: number;
  cisProContainerId: string;
  cisProLocation: string;
  specificGravity?: number | null;
  ph?: number | null;
  chlorides?: number | null;
  comments?: string;
  sampleRefNum?: string;
  recvAmount?: number | null;
  containerWeightId?: number | null;
  contWeightAmount?: number | null;
  otherWeight?: number | null;
  minAmount?: number | null;
  rigName?: string;
  recvDate?: string | null;
  trackSample?: boolean;
  notifyAtReorder?: boolean;
}

export interface UpdateEmailSubscriptionItemDto {
  emailTypeId: number;
  isSelected: boolean;
}
export interface UpdateMaterialRequest {
  name: string;
  functionName: string;
  materialTypes: number;
  specificGravity: number | null;
  sackWeight: number | null;
  addAfterStirring: boolean;
  isActive: boolean;
  isFluidLossTestWithFoamNeeded: boolean;
}

export interface GetMaterialByIdDto {
  materialId: number;
  materialName: string;
  functionName: string;
  specificGravity: number | null;
  materialTypeID: number;
  sackWeightLb: number | null;
  addAfterStirring: boolean;
  isActive: boolean;
  isFluidLossTestWithFoamNeeded: boolean;
}

export interface UpdateEmailSubscriptionsRequest {
  subscriptions: UpdateEmailSubscriptionItemDto[];
}
// ── Scale / Serial Ports DTOs ─────────────────────────────────────────────

export interface ScalePortConfigDto {
  currentPort: string;
  availablePorts: string[];
}

/** Mirrors CemSysApi.Common.DTOs.Tools.ScaleWeightDto */
export interface ScaleWeightDto {
  /** Whether the weight was captured successfully. */
  success: boolean;
  /** Weight value reported by the scale (0 when unsuccessful). */
  weight: number;
  /** Unit reported by the scale (e.g. "g", "kg"). */
  unit: string;
  /** True when the scale reported a stable ("S S") reading. */
  stable: boolean;
  /** COM port the reading was captured from. */
  port: string;
  /** Populated when success is false. */
  errorMessage?: string | null;
}

/** Request body for POST /tools/scale/weight — weight captured by the browser */
// export interface PostScaleWeightRequestDto {
//   weight: number;
//   unit: string;
//   stable: boolean;
//   /** COM port name as entered by the user (e.g. "COM5") */
//   port: string;
// }

@Injectable({
  providedIn: 'root'
})
export class ToolsService {

  private readonly baseUrl = `${environment.apiUrl}/tools`;

  constructor(private http: HttpClient) { }

  // ── Notes ────────────────────────────────────────────────────────────────

  saveNote(request: SaveNoteNotificationRequest): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/notes/create-note`, request);
  }

  // ── Rigs ─────────────────────────────────────────────────────────────────

  addRig(request: AddRigRequest): Observable<RigDto> {
    return this.http.post<RigDto>(`${this.baseUrl}/rigs`, request);
  }

  // ── Metrics ──────────────────────────────────────────────────────────────

  getSbus(): Observable<SbuDto[]> {
    return this.http.get<SbuDto[]>(`${this.baseUrl}/metrics/sbus`);
  }

  getTestMetrics(from: string, to: string, sbuCode?: string): Observable<TestMetricsResultDto> {
    const params: Record<string, string> = { from, to };
    if (sbuCode) params['sbuCode'] = sbuCode;
    return this.http.get<TestMetricsResultDto>(`${this.baseUrl}/metrics/tests`, { params });
  }

  // ── Well Update ───────────────────────────────────────────────────────────

  getWells(): Observable<WellDto[]> {
    return this.http.get<WellDto[]>(`${this.baseUrl}/well-update/wells`);
  }

  getProjectsByYear(year: number): Observable<WellUpdateProjectDto[]> {
    return this.http.get<WellUpdateProjectDto[]>(`${this.baseUrl}/well-update/projects`, {
      params: { year: year.toString() }
    });
  }

  changeProjectWell(projectId: number, request: ChangeProjectWellRequest): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/well-update/projects/${projectId}/well`, request);
  }

  updateWellInfo(wellId: number, request: UpdateWellInfoRequest): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/well-update/wells/${wellId}`, request);
  }

  // BU Redesign Metrics ──────────────────────────────────────────────────────────────

  getReport(from: string, to: string, sbuCode?: string): Observable<BuRedesignMetricsResultDto> {
    let params = new HttpParams().set('from', from).set('to', to);
    if (sbuCode) params = params.set('sbuCode', sbuCode);
    return this.http.get<BuRedesignMetricsResultDto>(`${this.baseUrl}/metrics/bu-redesign`, { params });
  }
  // Materials QAQC
    searchMaterialsQaqc(searchParams: MaterialsQaqcSearchParams): Observable<MaterialsQaqcDto[]> {
  let params = new HttpParams();
  
  if (searchParams.materialName) {
    params = params.set('materialName', searchParams.materialName);
  }
  if (searchParams.barcodeId) {
    params = params.set('barcodeId', searchParams.barcodeId);
  }
  if (searchParams.sampleRefNum) {
    params = params.set('sampleRefNum', searchParams.sampleRefNum);
  }
  if (searchParams.rigName) {
    params = params.set('rigName', searchParams.rigName);
  }

  return this.http.get<MaterialsQaqcDto[]>(`${this.baseUrl}/materials/search`, { params });
}

  // Update Material QAQC
  updateMaterialQaqc(request: UpdateMaterialRequestQaqc): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/materials/${request.id}`, request);
  }

  // Add Material QAQC
  addMaterialQaqc(request: AddMaterialRequestQaqc): Observable<MaterialsQaqcDto> {
    return this.http.post<MaterialsQaqcDto>(`${this.baseUrl}/materials`, request);
  }

  //  Search ──────────────────────────────────────────────────────────────

  searchProjects(request: ProjectSearchRequest): Observable<ProjectSearchResult[]> {
    return this.http.post<ProjectSearchResult[]>(`${this.baseUrl}/search/projects`, request);
  }

  // ── Email Subscriptions ───────────────────────────────────────────────────

  getEmailSubscriptions(): Observable<EmailSubscriptionsResponseDto> {
    return this.http.get<EmailSubscriptionsResponseDto>(`${this.baseUrl}/email-subscriptions`);
  }

  updateEmailSubscriptions(request: UpdateEmailSubscriptionsRequest): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/email-subscriptions`, request);
  }
  // ── Materials Management ──────────────────────────────────────────────────

  searchMaterials(request: SearchMaterialsRequest): Observable<GetMaterialsForSearchResultDto[]> {
    return this.http.post<GetMaterialsForSearchResultDto[]>(`${this.baseUrl}/manage-materials/search`, request);
  }

  createMaterial(request: CreateMaterialRequest): Observable<MaterialBriefDto> {
    return this.http.post<MaterialBriefDto>(`${this.baseUrl}/manage-materials`, request);
  }

  updateMaterial(materialId: number, request: UpdateMaterialRequest): Observable<MaterialBriefDto> {
    return this.http.put<MaterialBriefDto>(`${this.baseUrl}/manage-materials/${materialId}`, request);
  }
  // ── Scale / Serial Ports ─────────────────────────────────────────────────

  getScalePortConfig(): Observable<ScalePortConfigDto> {
    return this.http.get<ScalePortConfigDto>(`${this.baseUrl}/scale/port`);
  }

  saveScalePort(port: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/scale/port`, { port });
  }

    /**
   * Posts a weight reading (captured by the browser via Web Serial API) to the API for logging/persistence.
   * The browser reads the scale directly — the API never opens a COM port on Azure.
   */
  // postScaleWeight(request: PostScaleWeightRequestDto): Observable<ScaleWeightDto> {
  //   return this.http.post<ScaleWeightDto>(`${this.baseUrl}/scale/weight`, request);
  // }
  
  // ── Summary Report ────────────────────────────────────────────────────────

  getSummaryReportByDate(from: string, to: string, projectName?: string): Observable<SummaryReportDto[]> {
    const params: Record<string, string> = { from, to };
    if (projectName) params['projectName'] = projectName;
    return this.http.get<SummaryReportDto[]>(`${this.baseUrl}/summary-report`, { params });
  }
}
